import type {
  CreatePositionFormDTO,
  ReplacePositionSalaryScaleDTO,
  UpdatePositionFormDTO,
} from "@/modules/positions/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";

const baseUrl = `${config.apiUrl}/positions`;

export const positionsApi = {
  async list(query: Partial<OffsetPageQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}?${params.toString()}`;
    const res = await fetch(url, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch positions: ${res.statusText}`);
    return res.json();
  },

  async get(id: string) {
    const res = await fetch(`${baseUrl}/${id}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to fetch position: ${res.statusText}`);
    return res.json();
  },

  async create(input: CreatePositionFormDTO) {
    const formData = new FormData();
    formData.set("title", input.title);
    formData.set("departmentId", input.departmentId);
    if (input.parentId) formData.set("parentId", input.parentId);
    // Always send level, default to 1 if not provided
    formData.set("level", String(input.level ?? 1));
    if (input.maxEmployees !== null && input.maxEmployees !== undefined) {
      formData.set("maxEmployees", String(input.maxEmployees));
    }

    const res = await fetch(baseUrl, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to create position: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async update(id: string, input: UpdatePositionFormDTO) {
    const formData = new FormData();
    if (input.title) formData.set("title", input.title);
    if (input.departmentId) formData.set("departmentId", input.departmentId);
    if (input.parentId !== undefined) {
      formData.set("parentId", input.parentId ?? "");
    }
    if (input.level) formData.set("level", String(input.level));
    if (input.maxEmployees !== null && input.maxEmployees !== undefined) {
      formData.set("maxEmployees", String(input.maxEmployees));
    }

    const res = await fetch(`${baseUrl}/${id}`, {
      method: "PUT",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to update position: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async deactivate(id: string) {
    const res = await fetch(`${baseUrl}/${id}/deactivate`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to deactivate position: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async reactivate(id: string) {
    const res = await fetch(`${baseUrl}/${id}/reactivate`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to reactivate position: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async getSalaryBands(positionId: string) {
    const res = await fetch(`${baseUrl}/${positionId}/salary-bands`, {
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch salary bands: ${res.statusText}`);
    }
    return res.json();
  },

  async replaceSalaryBands(
    positionId: string,
    input: ReplacePositionSalaryScaleDTO,
  ) {
    const res = await fetch(`${baseUrl}/${positionId}/salary-bands`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
      credentials: "include",
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        typeof err.error === "string"
          ? err.error
          : `Failed to save salary bands: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async remove(id: string) {
    const res = await fetch(`${baseUrl}/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete position: ${res.statusText}`,
      );
    }
    return res.json();
  },
};
